import React, { useState } from 'react';
import {
  Avatar as MuiAvatar,
  Box,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Alert,
  Snackbar,
} from '@mui/material';
import {
  PhotoCamera,
  Delete,
  Person,
} from '@mui/icons-material';
import ImageUploadCrop from './ImageUploadCrop';
import avatarService from '../../services/avatarService';
import { useAuth } from '../../contexts/AuthContext';

interface AvatarProps {
  src?: string;
  size?: number;
  showUploadButton?: boolean;
  editable?: boolean;
  sx?: any;
}

const Avatar: React.FC<AvatarProps> = ({
  src ,
  size = 120,
  showUploadButton = true,
  editable = true,
  sx = {},
}) => {
  const { user, updateProfile } = useAuth();
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [notification, setNotification] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const avatarUrl = avatarService.getAvatarUrl(src);
  const initials = avatarService.getInitials(user?.nome || '');

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    if (editable) {
      setMenuAnchor(event.currentTarget);
    }
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  const handleUploadClick = () => {
    setUploadModalOpen(true);
    handleMenuClose();
  };

  const handleRemoveAvatar = async () => {
    setUploading(true);
    handleMenuClose();

    try {
      const response = await avatarService.removeAvatar();
      
      if (response.success) {
        // Atualizar o contexto do usuário
        await updateProfile({ avatar: '' });
        
        setNotification({
          open: true,
          message: 'Avatar removido com sucesso!',
          severity: 'success',
        });
      } else {
        setNotification({
          open: true,
          message: response.message || 'Erro ao remover avatar',
          severity: 'error',
        });
      }
    } catch (error) {
      console.error('Erro ao remover avatar:', error);
      setNotification({
        open: true,
        message: 'Erro ao remover avatar',
        severity: 'error',
      });
    } finally {
      setUploading(false);
    }
  };

  const handleImageSave = async (croppedImageBlob: Blob, croppedImageUrl: string) => {
    setUploading(true);
    setUploadProgress(0);

    try {
      const response = await avatarService.uploadAvatar(
        croppedImageBlob,
        (progress) => setUploadProgress(progress)
      );

      if (response.success && response.data) {
        // Atualizar o contexto do usuário
        await updateProfile({ avatar: response.data.filename });
        
        setNotification({
          open: true,
          message: 'Avatar atualizado com sucesso!',
          severity: 'success',
        });
      } else {
        setNotification({
          open: true,
          message: response.message || 'Erro ao fazer upload do avatar',
          severity: 'error',
        });
      }
    } catch (error) {
      console.error('Erro ao fazer upload do avatar:', error);
      setNotification({
        open: true,
        message: 'Erro ao fazer upload do avatar',
        severity: 'error',
      });
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleCloseNotification = () => {
    setNotification(prev => ({ ...prev, open: false }));
  };

  return (
    <>
      <Box sx={{ position: 'relative', display: 'inline-block', ...sx }}>
        <MuiAvatar
          src={avatarUrl}
          sx={{
            width: size,
            height: size,
            bgcolor: 'primary.main',
            fontSize: size / 3,
            cursor: editable ? 'pointer' : 'default',
            transition: 'all 0.2s ease-in-out',
            '&:hover': editable ? {
              transform: 'scale(1.05)',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            } : {},
          }}
          onClick={handleMenuOpen}
        >
          {!avatarUrl && initials}
        </MuiAvatar>

        {/* Loading Overlay */}
        {uploading && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(0, 0, 0, 0.7)',
              borderRadius: '50%',
              color: 'white',
            }}
          >
            <CircularProgress
              size={size / 3}
              color="inherit"
              variant={uploadProgress > 0 ? 'determinate' : 'indeterminate'}
              value={uploadProgress}
            />
          </Box>
        )}

        {/* Upload Button */}
        {showUploadButton && editable && !uploading && (
          <Tooltip title="Alterar foto">
            <IconButton
              sx={{
                position: 'absolute',
                bottom: 0,
                right: 0,
                backgroundColor: 'primary.main',
                color: 'white',
                width: size / 4,
                height: size / 4,
                '&:hover': {
                  backgroundColor: 'primary.dark',
                },
                boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
              }}
              onClick={handleUploadClick}
              size="small"
            >
              <PhotoCamera sx={{ fontSize: size / 8 }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/* Context Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
        transformOrigin={{ horizontal: 'center', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'center', vertical: 'bottom' }}
      >
        <MenuItem onClick={handleUploadClick}>
          <ListItemIcon>
            <PhotoCamera fontSize="small" />
          </ListItemIcon>
          <ListItemText>
            {avatarUrl ? 'Alterar Foto' : 'Adicionar Foto'}
          </ListItemText>
        </MenuItem>
        
        {avatarUrl && (
          <MenuItem onClick={handleRemoveAvatar}>
            <ListItemIcon>
              <Delete fontSize="small" />
            </ListItemIcon>
            <ListItemText>Remover Foto</ListItemText>
          </MenuItem>
        )}
      </Menu>

      {/* Upload Modal */}
      <ImageUploadCrop
        open={uploadModalOpen}
        onClose={() => setUploadModalOpen(false)}
        onImageSave={handleImageSave}
        title="Upload de Avatar"
        aspectRatio={1} // Quadrado
        maxWidth={400}
        maxHeight={400}
        quality={0.9}
        outputFormat="auto"
        circularCrop={true}
        minCropSize={100}
      />

      {/* Notification */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default Avatar; 